// ── Core Erlang B/C/A Engine ──
// All formulas use numerically stable recursion (no factorials)
// Erlang A uses exact M/M/N+M stationary distribution

import type { ErlangCInputs, ErlangCResults, ErlangAInputs, ErlangAResults } from '../types/erlang';

/**
 * Erlang B — probability all servers busy (blocked call)
 * Jagerman recursion: B(0)=1; B(i) = A*B(i-1) / (i + A*B(i-1))
 */
export function erlangB(servers: number, trafficIntensity: number): number {
  if (servers < 0 || trafficIntensity < 0) return 0;
  let b = 1.0;
  for (let i = 1; i <= servers; i++) {
    b = (trafficIntensity * b) / (i + trafficIntensity * b);
  }
  return b;
}

/**
 * Erlang C — probability of waiting (all servers busy, call queues)
 * Pw = N * B(N,A) / (N - A * (1 - B(N,A)))
 */
export function erlangC(servers: number, trafficIntensity: number): number {
  if (servers <= trafficIntensity) return 1.0;
  const eb = erlangB(servers, trafficIntensity);
  const denominator = servers - trafficIntensity * (1 - eb);
  if (denominator <= 0) return 1.0;
  return (servers * eb) / denominator;
}

/**
 * Service Level (Erlang C)
 * SL = 1 - Pw * e^(-(N-A)*t/AHT)
 */
export function serviceLevel(
  servers: number,
  trafficIntensity: number,
  targetTime: number,
  aht: number
): number {
  if (servers <= trafficIntensity) return 0;
  const pw = erlangC(servers, trafficIntensity);
  const exponent = -((servers - trafficIntensity) * targetTime) / aht;
  return 1 - pw * Math.exp(exponent);
}

/**
 * Average Speed of Answer (Erlang C)
 * ASA = Pw * AHT / (N - A)
 */
export function asa(servers: number, trafficIntensity: number, aht: number): number {
  if (servers <= trafficIntensity) return Infinity;
  const pw = erlangC(servers, trafficIntensity);
  return (pw * aht) / (servers - trafficIntensity);
}

/**
 * Average queue length (Erlang C)
 * Lq = Pw * A / (N - A)
 */
export function avgQueueLength(servers: number, trafficIntensity: number): number {
  if (servers <= trafficIntensity) return Infinity;
  const pw = erlangC(servers, trafficIntensity);
  return (pw * trafficIntensity) / (servers - trafficIntensity);
}

/**
 * Find minimum agents to meet SL target (Erlang C)
 */
export function findMinAgentsC(
  trafficIntensity: number,
  targetSL: number,
  targetTime: number,
  aht: number
): number {
  let agents = Math.max(1, Math.ceil(trafficIntensity) + 1);
  for (; agents < trafficIntensity + 200; agents++) {
    const sl = serviceLevel(agents, trafficIntensity, targetTime, aht);
    if (sl >= targetSL) return agents;
  }
  return agents;
}

// ── Erlang A (M/M/N+M — Abandonment) ──
// Exact M/M/N+M metrics, numerically stable for any load (no factorials, no powers).

/**
 * Exact Erlang A (M/M/N+M) metrics.
 *
 * N agents, arrival rate λ = A/AHT, handle rate 1/AHT, and each waiting caller gives up
 * at rate 1/APT. With h = AHT/APT and τ = T/AHT, relative to "all agents busy, nobody waiting":
 *   q₀ = 1, q_k = q_{k−1} · A / (N + k·h)            (k callers waiting)
 *   states with a free agent sum to (1/B − 1), B = Erlang B(N, A)
 *   Z = 1/B − 1 + Σ q_k
 * A caller who finds k waiting reaches an agent after k+1 "advances", each at rate N + m·h,
 * while giving up at rate h. So, with F_k a negative-binomial CDF
 * (x = 1 − e^{−hτ}, b = N/h + 1, t₀ = e^{−(N+h)τ}, t_j = t_{j−1}·x·(b+j−1)/j):
 *   P(wait)      = Σ q_k / Z
 *   P(abandon)   = Σ q_k · (k+1)h / (N+(k+1)h) / Z
 *   Service level = answered within T ÷ ALL offered (abandoners count as misses)
 *                = [ (1/B − 1) + Σ q_k · N/(N+(k+1)h) · (1 − F_k) ] / Z
 *   ASA (answered callers) = AHT · Σ q_k · N/(N+(k+1)h) · Σ_{j=1..k+1} 1/(N+jh) / (Z · (1 − P(abandon)))
 *   E[queue] = P(abandon) · A / h                     (abandon rate = θ·E[Nq]/λ)
 * Sums are log-scaled so loads of thousands of Erlangs do not overflow; the series runs past
 * the queue's peak (k* = (A−N)/h) so overloaded queues are exact too. Very patient callers
 * (APT → ∞) reproduce Erlang C; very impatient ones reproduce Erlang B.
 */
export function erlangAMetrics(
  N: number,
  A: number,
  AHT: number,
  APT: number,
  targetTime = 0
): { pw: number; abandonRate: number; serviceLevel: number; asa: number; eNq: number } {
  if (A <= 0 || AHT <= 0) return { pw: 0, abandonRate: 0, serviceLevel: 1, asa: 0, eNq: 0 };
  if (N <= 0) return { pw: 1, abandonRate: 1, serviceLevel: 0, asa: 0, eNq: Infinity };
  const h = Math.max(AHT / Math.max(APT, 1e-9), 1e-9);
  const tau = Math.max(0, targetTime) / AHT;
  const B = erlangB(N, A);
  if (B < 1e-300) return { pw: 0, abandonRate: 0, serviceLevel: 1, asa: 0, eNq: 0 };
  const x = -Math.expm1(-h * tau);
  const bb = N / h + 1;
  let q = 1;
  let Z0 = 1 / B - 1;
  let Sw = 0;
  let Sab = 0;
  let Ssl = 0;
  let Sasa = 0;
  let H = 0;
  let tS = 1; // scaled negative-binomial term; actual = tS · e^{logS}
  let Fs = 0;
  let logS = -(N + h) * tau;
  const kStar = Math.max(0, (A - N) / h);
  const kMax = Math.min(2_000_000, Math.ceil(kStar + 9 * Math.sqrt(A / h) + 200));
  for (let k = 0; k <= kMax; k++) {
    Fs += tS;
    const cdf = Math.max(0, 1 - Fs * Math.exp(logS));
    const denom = N + (k + 1) * h;
    const ps = N / denom;
    H += 1 / denom;
    Sw += q;
    Ssl += q * ps * cdf;
    Sab += q * (1 - ps);
    Sasa += q * ps * H;
    tS *= (x * (bb + k)) / (k + 1);
    if (tS > 1e200) {
      tS *= 1e-200;
      Fs *= 1e-200;
      logS += 200 * Math.LN10;
    }
    const r = A / denom;
    q *= r;
    if (k + 1 > kStar && r < 1 && (q * r) / (1 - r) < 1e-16 * (Z0 + Sw)) break;
    if (q > 1e250) {
      q *= 1e-250;
      Z0 *= 1e-250;
      Sw *= 1e-250;
      Ssl *= 1e-250;
      Sab *= 1e-250;
      Sasa *= 1e-250;
    }
  }
  const Z = Z0 + Sw;
  const ab = Math.min(1, Math.max(0, Sab / Z));
  return {
    pw: Math.min(1, Sw / Z),
    abandonRate: ab,
    serviceLevel: Math.min(1, Math.max(0, (Z0 + Ssl) / Z)),
    asa: ab < 1 ? (AHT * Sasa) / (Z * (1 - ab)) : Infinity,
    eNq: (ab * A) / h,
  };
}

/**
 * Erlang A probability of waiting, abandonment, ASA of answered callers (seconds) and mean
 * queue length. Kept for existing callers; see erlangAMetrics.
 */
export function erlangACompute(
  N: number,
  A: number,
  AHT: number,
  APT: number
): { pw: number; abandonRate: number; avgWait: number; eNq: number } {
  const m = erlangAMetrics(N, A, AHT, APT, 0);
  return { pw: m.pw, abandonRate: m.abandonRate, avgWait: m.asa, eNq: m.eNq };
}

/**
 * Erlang A service level: callers answered within the target time ÷ all offered calls.
 * Callers who abandon count as misses, so this is never flattered by abandonment.
 */
export function serviceLevelA(
  N: number,
  A: number,
  AHT: number,
  APT: number,
  targetTime: number
): number {
  return erlangAMetrics(N, A, AHT, APT, targetTime).serviceLevel;
}

/**
 * Find minimum agents to meet SL target (Erlang A)
 */
export function findMinAgentsA(
  trafficIntensity: number,
  targetSL: number,
  targetTime: number,
  aht: number,
  avgPatienceTime: number
): number {
  // Service level rises with agents; under Erlang A the answer can be below the load
  // (callers who give up relieve the queue), so search from 1 upwards by galloping + bisection.
  const t = Math.min(targetSL, 0.9999);
  const ok = (n: number) => serviceLevelA(n, trafficIntensity, aht, avgPatienceTime, targetTime) >= t;
  let hi = Math.max(1, Math.ceil(trafficIntensity));
  while (!ok(hi) && hi < 1e6) hi *= 2;
  let lo = 0;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (ok(mid)) hi = mid;
    else lo = mid;
  }
  return hi;
}

// ── Full Calculation Functions ──

export function calculateErlangC(inputs: ErlangCInputs): ErlangCResults {
  const {
    callsPerInterval,
    ahtSeconds,
    intervalMinutes,
    targetSLPercent,
    targetAnswerTime,
    shrinkagePercent,
    costPerAgentHour,
  } = inputs;

  const intervalSeconds = intervalMinutes * 60;
  const trafficIntensity = (callsPerInterval * ahtSeconds) / intervalSeconds;
  const targetSL = targetSLPercent / 100;
  const shrinkage = shrinkagePercent / 100;

  const rawAgents = findMinAgentsC(trafficIntensity, targetSL, targetAnswerTime, ahtSeconds);
  const rosteredAgents = Math.ceil(rawAgents / (1 - shrinkage));

  const sl = serviceLevel(rawAgents, trafficIntensity, targetAnswerTime, ahtSeconds);
  const asaVal = asa(rawAgents, trafficIntensity, ahtSeconds);
  const pw = erlangC(rawAgents, trafficIntensity);
  const occ = trafficIntensity / rawAgents;
  const immediateAnswer = 1 - pw;
  const queueLen = avgQueueLength(rawAgents, trafficIntensity);

  const agentHoursPerInterval = rosteredAgents * (intervalMinutes / 60);
  const costPerInterval = agentHoursPerInterval * costPerAgentHour;
  const costPerHour = rosteredAgents * costPerAgentHour;

  return {
    trafficIntensity,
    rawAgents,
    rosteredAgents,
    serviceLevel: sl,
    asa: asaVal,
    probabilityWait: pw,
    occupancy: occ,
    immediateAnswer,
    avgQueueLength: queueLen,
    costPerInterval,
    costPerHour,
  };
}

export function calculateErlangA(inputs: ErlangAInputs): ErlangAResults {
  const {
    callsPerInterval,
    ahtSeconds,
    intervalMinutes,
    targetSLPercent,
    targetAnswerTime,
    shrinkagePercent,
    costPerAgentHour,
    avgPatienceTime,
  } = inputs;

  const intervalSeconds = intervalMinutes * 60;
  const trafficIntensity = (callsPerInterval * ahtSeconds) / intervalSeconds;
  const targetSL = targetSLPercent / 100;
  const shrinkage = shrinkagePercent / 100;

  // Erlang C for comparison
  const rawAgentsC = findMinAgentsC(trafficIntensity, targetSL, targetAnswerTime, ahtSeconds);

  // Erlang A
  const rawAgents = findMinAgentsA(trafficIntensity, targetSL, targetAnswerTime, ahtSeconds, avgPatienceTime);

  const rosteredAgents = Math.ceil(rawAgents / (1 - shrinkage));

  const aResult = erlangACompute(rawAgents, trafficIntensity, ahtSeconds, avgPatienceTime);
  const sl = serviceLevelA(rawAgents, trafficIntensity, ahtSeconds, avgPatienceTime, targetAnswerTime);
  const pw = aResult.pw;
  // callers who abandon are never handled, so agents only carry the answered share of the load
  const occ = (trafficIntensity * (1 - aResult.abandonRate)) / rawAgents;

  const agentHoursPerInterval = rosteredAgents * (intervalMinutes / 60);
  const costPerInterval = agentHoursPerInterval * costPerAgentHour;
  const costPerHour = rosteredAgents * costPerAgentHour;
  const callsAbandoned = Math.round(callsPerInterval * aResult.abandonRate);

  return {
    trafficIntensity,
    rawAgents,
    rosteredAgents,
    serviceLevel: sl,
    asa: aResult.avgWait,
    probabilityWait: pw,
    occupancy: occ,
    immediateAnswer: 1 - pw,
    avgQueueLength: aResult.eNq,
    costPerInterval,
    costPerHour,
    abandonRate: aResult.abandonRate,
    callsAbandoned,
    rawAgentsErlangC: rawAgentsC,
    agentSavings: rawAgentsC - rawAgents,
  };
}

/**
 * Generate staffing curve data: agents from min to max vs SL
 */
export function staffingCurve(
  trafficIntensity: number,
  targetTime: number,
  aht: number,
  targetSL: number,
  avgPatienceTime?: number
): { agents: number; sl: number; slA?: number }[] {
  const minAgents = Math.max(1, Math.ceil(trafficIntensity) - 2);
  const maxAgents = Math.ceil(trafficIntensity) + 30;
  const points: { agents: number; sl: number; slA?: number }[] = [];

  for (let n = minAgents; n <= maxAgents; n++) {
    const slC = serviceLevel(n, trafficIntensity, targetTime, aht);
    let slA: number | undefined;
    if (avgPatienceTime) {
      slA = serviceLevelA(n, trafficIntensity, aht, avgPatienceTime, targetTime);
    }
    points.push({ agents: n, sl: slC, slA });
    if (slC > 0.995) break;
  }

  return points;
}
